import React, { createContext, useContext, useState, useEffect } from 'react';

import { useStoreBootstrap } from './StoreBootstrap';

export type Language = 'pl' | 'en' | 'de' | 'fr' | 'es' | 'it' | 'cs' | 'sk' | 'nl' | 'ro' | 'uk' | 'hu' | 'hr' | 'lt' | 'lv' | 'et';

export type IntlRate = {
  country_code: string;
  country_name: string;
  courier: string;
  price_eur: number;
  price_pln: number | null;
  estimated_days: string;
  active?: number;
};

export type VatRate = {
  country_code: string;
  country_name: string;
  vat_rate: number; // e.g. 0.23
  is_eu: number;
};

export type OssSettings = {
  id: number;
  oss_threshold: number;      // default 10000
  annual_eur_sales: number;   // running total this year
  current_year: number;
  use_destination_vat: number; // 0 = Polish 23%, 1 = destination country VAT
  manually_overridden: number;
  updated_at: string | null;
};

// Mapowanie: kod języka → domyślny kraj wysyłki (ISO)
export const LANG_TO_COUNTRY: Partial<Record<Language, string>> = {
  de: 'DE',
  cs: 'CZ',
  sk: 'SK',
  fr: 'FR',
  nl: 'NL',
  it: 'IT',
  ro: 'RO',
  hu: 'HU',
  hr: 'HR',
  lt: 'LT',
  lv: 'LV',
  et: 'EE',
};

// Mapowanie: kraj ISO → język do wyświetlenia w przełączniku
export const COUNTRY_TO_LANG: Record<string, Language> = {
  DE: 'de', AT: 'de',
  CZ: 'cs',
  SK: 'sk',
  FR: 'fr', BE: 'fr',
  NL: 'nl',
  IT: 'it',
  RO: 'ro',
  HU: 'hu',
  HR: 'hr',
  LT: 'lt',
  LV: 'lv',
  EE: 'et',
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  isEurMode: boolean;
  intlRates: IntlRate[];
  selectedShippingCountry: string;
  setSelectedShippingCountry: (code: string) => void;
  vatRates: VatRate[];
  ossSettings: OssSettings | null;
  // Zwraca cenę EUR brutto z prawidłową stawką VAT dla wybranego kraju
  applyEurVat: (priceEurNetto: number) => number;
  // Aktualna stawka VAT (0.23 dla PL, lub stawka kraju docelowego jeśli OSS aktywne)
  currentVatRate: number;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const translations: Record<Language, Record<string, string>> = {
  pl: {
    'home': 'Strona główna',
    'products': 'Produkty',
    'contact': 'Kontakt',
    'cart': 'Koszyk',
    'search': 'Szukaj',
    'add_to_cart': 'Dodaj do koszyka',
    'price': 'Cena',
    'availability': 'Dostępność',
    'in_stock': 'Dostępne',
    'out_of_stock': 'Brak w magazynie',
  },
  en: {
    'home': 'Home',
    'products': 'Products',
    'contact': 'Contact',
    'cart': 'Cart',
    'search': 'Search',
    'add_to_cart': 'Add to cart',
    'price': 'Price',
    'availability': 'Availability',
    'in_stock': 'In stock',
    'out_of_stock': 'Out of stock',
  },
  de: {
    'home': 'Startseite',
    'products': 'Produkte',
    'contact': 'Kontakt',
    'cart': 'Warenkorb',
    'search': 'Suche',
    'add_to_cart': 'In den Warenkorb',
    'price': 'Preis',
    'availability': 'Verfügbarkeit',
    'in_stock': 'Auf Lager',
    'out_of_stock': 'Nicht vorrätig',
  },
  fr: {
    'home': 'Accueil',
    'products': 'Produits',
    'contact': 'Contact',
    'cart': 'Panier',
    'search': 'Rechercher',
    'add_to_cart': 'Ajouter au panier',
    'price': 'Prix',
    'availability': 'Disponibilité',
    'in_stock': 'En stock',
    'out_of_stock': 'Rupture de stock',
  },
  es: {
    'home': 'Inicio',
    'products': 'Productos',
    'contact': 'Contacto',
    'cart': 'Carrito',
    'search': 'Buscar',
    'add_to_cart': 'Añadir al carrito',
    'price': 'Precio',
    'availability': 'Disponibilidad',
    'in_stock': 'En stock',
    'out_of_stock': 'Agotado',
  },
  it: {
    'home': 'Home',
    'products': 'Prodotti',
    'contact': 'Contatto',
    'cart': 'Carrello',
    'search': 'Cerca',
    'add_to_cart': 'Aggiungi al carrello',
    'price': 'Prezzo',
    'availability': 'Disponibilità',
    'in_stock': 'Disponibile',
    'out_of_stock': 'Esaurito',
  },
  cs: {
    'home': 'Domů',
    'products': 'Produkty',
    'contact': 'Kontakt',
    'cart': 'Košík',
    'search': 'Hledat',
    'add_to_cart': 'Přidat do košíku',
    'price': 'Cena',
    'availability': 'Dostupnost',
    'in_stock': 'Skladem',
    'out_of_stock': 'Vyprodáno',
  },
  sk: {
    'home': 'Domov',
    'products': 'Produkty',
    'contact': 'Kontakt',
    'cart': 'Košík',
    'search': 'Hľadať',
    'add_to_cart': 'Pridať do košíka',
    'price': 'Cena',
    'availability': 'Dostupnosť',
    'in_stock': 'Skladom',
    'out_of_stock': 'Vypredané',
  },
  nl: {
    'home': 'Home',
    'products': 'Producten',
    'contact': 'Contact',
    'cart': 'Winkelwagen',
    'search': 'Zoeken',
    'add_to_cart': 'Toevoegen aan winkelwagen',
    'price': 'Prijs',
    'availability': 'Beschikbaarheid',
    'in_stock': 'Op voorraad',
    'out_of_stock': 'Uitverkocht',
  },
  ro: {
    'home': 'Acasă',
    'products': 'Produse',
    'contact': 'Contact',
    'cart': 'Coș',
    'search': 'Căutare',
    'add_to_cart': 'Adaugă în coș',
    'price': 'Preț',
    'availability': 'Disponibilitate',
    'in_stock': 'În stoc',
    'out_of_stock': 'Epuizat',
  },
  uk: {
    'home': 'Головна',
    'products': 'Продукти',
    'contact': 'Контакт',
    'cart': 'Кошик',
    'search': 'Пошук',
    'add_to_cart': 'Додати до кошика',
    'price': 'Ціна',
    'availability': 'Доступність',
    'in_stock': 'В наявності',
    'out_of_stock': 'Немає в наявності',
  },
  hu: {
    'home': 'Főoldal',
    'products': 'Termékek',
    'contact': 'Kapcsolat',
    'cart': 'Kosár',
    'search': 'Keresés',
    'add_to_cart': 'Kosárba',
    'price': 'Ár',
    'availability': 'Elérhetőség',
    'in_stock': 'Raktáron',
    'out_of_stock': 'Elfogyott',
  },
  hr: {
    'home': 'Početna',
    'products': 'Proizvodi',
    'contact': 'Kontakt',
    'cart': 'Košarica',
    'search': 'Pretraži',
    'add_to_cart': 'Dodaj u košaricu',
    'price': 'Cijena',
    'availability': 'Dostupnost',
    'in_stock': 'Na zalihi',
    'out_of_stock': 'Nije dostupno',
  },
  lt: {
    'home': 'Pradžia',
    'products': 'Produktai',
    'contact': 'Kontaktai',
    'cart': 'Krepšelis',
    'search': 'Ieškoti',
    'add_to_cart': 'Į krepšelį',
    'price': 'Kaina',
    'availability': 'Prieinamumas',
    'in_stock': 'Yra sandėlyje',
    'out_of_stock': 'Nėra sandėlyje',
  },
  lv: {
    'home': 'Sākums',
    'products': 'Produkti',
    'contact': 'Kontakti',
    'cart': 'Grozs',
    'search': 'Meklēt',
    'add_to_cart': 'Pievienot grozam',
    'price': 'Cena',
    'availability': 'Pieejamība',
    'in_stock': 'Noliktavā',
    'out_of_stock': 'Nav noliktavā',
  },
  et: {
    'home': 'Avaleht',
    'products': 'Tooted',
    'contact': 'Kontakt',
    'cart': 'Ostukorv',
    'search': 'Otsi',
    'add_to_cart': 'Lisa ostukorvi',
    'price': 'Hind',
    'availability': 'Saadavus',
    'in_stock': 'Laos olemas',
    'out_of_stock': 'Laost otsas',
  },
};

function doGTranslate(langCode: string) {
  const hostname = window.location.hostname;
  // Wyczyść stare cookie googtrans
  document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=${hostname}; path=/;`;
  document.cookie = `googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${hostname}; path=/;`;

  if (langCode === 'pl') {
    // Powrót do polskiego: wyczyść cookie i przeładuj
    window.location.reload();
    return;
  }

  // Ustaw cookie dla trwałości między przeładowaniami
  document.cookie = `googtrans=/pl/${langCode}; path=/;`;
  document.cookie = `googtrans=/pl/${langCode}; domain=${hostname}; path=/;`;
  document.cookie = `googtrans=/pl/${langCode}; domain=.${hostname}; path=/;`;

  // Wywołaj tłumaczenie bezpośrednio przez selektor GT (bez przeładowania strony)
  const tryTranslate = (attempt: number) => {
    const combo = document.querySelector('select.goog-te-combo') as HTMLSelectElement | null;
    if (combo) {
      combo.value = langCode;
      combo.dispatchEvent(new Event('change'));
      return;
    }
    if (attempt < 30) {
      setTimeout(() => tryTranslate(attempt + 1), 200);
    } else {
      // Ostateczny fallback: przeładuj z ustawionym cookie
      window.location.reload();
    }
  };
  tryTranslate(0);
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bootstrap = useStoreBootstrap();
  // Inicjalizacja synchroniczna z localStorage — zapobiega flashowi PLN→EUR po odświeżeniu
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      if (bootstrap) return 'pl';
      const saved = localStorage.getItem('language') as Language;
      return saved && translations[saved] ? saved : 'pl';
    } catch { return 'pl'; }
  });
  useEffect(() => {
    if (!bootstrap) return;
    const saved = localStorage.getItem('language') as Language;
    if (saved && translations[saved]) setLanguageState(saved);
  }, []);
  const [intlRates, setIntlRates] = useState<IntlRate[]>([]);
  // Persist selectedShippingCountry w localStorage — stabilność po odświeżeniu
  const [selectedShippingCountry, setSelectedShippingCountryRaw] = useState<string>(() => {
    try { return bootstrap ? '' : localStorage.getItem('shippingCountry') || ''; } catch { return ''; }
  });
  const setSelectedShippingCountry = (code: string) => {
    setSelectedShippingCountryRaw(code);
    try {
      if (code) localStorage.setItem('shippingCountry', code);
      else localStorage.removeItem('shippingCountry');
    } catch {}
  };
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [ossSettings, setOssSettings] = useState<OssSettings | null>(null);

  useEffect(() => {
    // Nie wywołuj doGTranslate przy inicjalizacji — cookie googtrans jest już ustawione
    // przez poprzedni reload, więc GT samo zastosuje tłumaczenie po załadowaniu strony.
    // Wywołanie doGTranslate tutaj spowodowałoby pętlę nieskończonych przeładowań.
    const initCountry = localStorage.getItem('shippingCountry') || '';
    fetch('/api/config/international-shipping', { cache: 'no-store' })
      .then(r => r.json())
      .then(j => {
        if (j?.ok && Array.isArray(j.rates)) {
          const active = j.rates.filter((r: IntlRate & { active?: number }) => r.active);
          setIntlRates(active);
          // Ustaw autoCountry tylko jeśli nie ma już zapisanego kraju w localStorage
          if (!initCountry) {
            const savedLang = localStorage.getItem('language') as Language;
            if (savedLang && savedLang !== 'pl') {
              const autoCountry = LANG_TO_COUNTRY[savedLang];
              if (autoCountry && active.some((r: IntlRate) => r.country_code === autoCountry)) {
                setSelectedShippingCountry(autoCountry);
              }
            }
          }
        }
      })
      .catch(() => {});
    fetch('/api/config/vat-rates', { cache: 'no-store' })
      .then(r => r.json())
      .then(j => {
        if (j?.ok) {
          if (Array.isArray(j.rates)) setVatRates(j.rates);
          if (j.oss) setOssSettings(j.oss as OssSettings);
        }
      })
      .catch(() => {});
  }, []);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
    doGTranslate(lang);
    if (lang !== 'pl') {
      const autoCountry = LANG_TO_COUNTRY[lang];
      if (autoCountry && intlRates.some(r => r.country_code === autoCountry)) {
        setSelectedShippingCountry(autoCountry);
      }
    } else {
      setSelectedShippingCountry('');
    }
  };

  // Wyznacz aktualną stawkę VAT wg OSS i wybranego kraju
  const currentVatRate: number = (() => {
    if (!ossSettings || !ossSettings.use_destination_vat) return 0.23; // poniżej progu: polska 23%
    if (!selectedShippingCountry || selectedShippingCountry === 'PL') return 0.23;
    const found = vatRates.find(v => v.country_code === selectedShippingCountry);
    return found ? found.vat_rate : 0.23;
  })();

  // Przelicz netto EUR → brutto EUR z prawidłowym VATem
  const applyEurVat = (priceEurNetto: number): number => {
    return Math.round(priceEurNetto * (1 + currentVatRate) * 100) / 100;
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  // EUR mode: non-PL language OR explicit non-PL shipping country selected
  const isEurMode = language !== 'pl' || (selectedShippingCountry !== '' && selectedShippingCountry !== 'PL');

  return (
    <LanguageContext.Provider value={{
      language, setLanguage, t,
      isEurMode,
      intlRates,
      selectedShippingCountry,
      setSelectedShippingCountry,
      vatRates,
      ossSettings,
      applyEurVat,
      currentVatRate,
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
